/*
 * This illustrates how to create a widget that makes use of a number of different field definition types.
 */

import { xCheckoutActions, xCheckoutState, xHeaderActions, xHeaderState } from "@eisinc/App/Store/Reducers";
import { StepperContext } from "@eisinc/App/Widgets/CheckoutStepper/StepperContext";
import { ButtonPresentationProps } from "@eisinc/MobiusOverrides/Button";
import Modal, { ModalPresentationProps } from "@eisinc/MobiusOverrides/Modal";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@eisinc/Overrides/Components/AddressInfoDisplay";
import Directions from "@eisinc/XDL/Icons/Directions";
import Edit from "@eisinc/XDL/Icons/Edit";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import updatePickUpWarehouse from "@insite/client-framework/Store/Context/Handlers/UpdatePickUpWarehouse";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { BillToModel, ShipToModel, WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import FindLocationModal, { FindLocationModalStyles } from "@insite/content-library/Components/FindLocationModal";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import { CheckoutReviewAndSubmitPageContext } from "@insite/content-library/Pages/CheckoutReviewAndSubmitPage";
import { CheckoutShippingPageContext } from "@insite/content-library/Pages/CheckoutShippingPage";
import BillingAddress, { BillingAddressStyles } from "@insite/content-library/Widgets/CheckoutShipping/BillingAddress";
import PickUpAddress, { PickUpAddressStyles } from "@insite/content-library/Widgets/CheckoutShipping/PickUpAddress";
import ShippingAddress, {
    ShippingAddressStyles,
} from "@insite/content-library/Widgets/CheckoutShipping/ShippingAddress";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { useContext } from "react";
import { connect, DispatchProp } from "react-redux";
import { css } from "styled-components";

export interface CheckoutReviewStyles {
    wrapper?: InjectableCss;
    editLink?: LinkPresentationProps;

    headingText?: TypographyPresentationProps;
    bodyText?: TypographyPresentationProps;
    valueText?: TypographyPresentationProps;
    ccText?: TypographyPresentationProps;
    billingAddress?: BillingAddressStyles;
    shippingAddress?: ShippingAddressStyles;
    pickUpAddress?: PickUpAddressStyles;
    addressDisplay?: AddressInfoDisplayStyles;

    gridItem?: GridItemProps;
    changeLink?: LinkPresentationProps;
    directionsLink?: LinkPresentationProps;
    findLocationModal?: FindLocationModalStyles;
    pickupInstructionsModal?: ModalPresentationProps;
}

export const styles: CheckoutReviewStyles = {
    wrapper: {
        css: css`
            position: relative;
            min-height: 100px;
            border-top: 2px solid ${getColor("common.ui2")};
            padding: 10px 0 25px;
        `,
    },
    editLink: {
        typographyProps: {
            size: "12px",
            lineHeight: "10px",
            css: css`
                color: ${getColor("text.main")} !important;
            `,
        },
        icon: {
            iconProps: {
                src: Edit,
                color: "primary.main",
                size: 14,
            },
            position: "left",
        },
        css: css`
            position: absolute;
            top: 10px;
            right: 10px;
            &:hover > span,
            &:focus > span {
                color: ${getColor("primary")} !important;
            }
        `,
    },
    headingText: {
        variant: "h5",
        css: css`
            margin-bottom: 15px;
            font-size: 24px;
            font-weight: 500;
        `,
    },
    bodyText: {
        css: css`
            margin-left: 5px;
            margin-bottom: 10px;
            white-space: nowrap;
        `,
    },
    valueText: {
        css: css`
            margin-left: 5px;
            margin-bottom: 10px;
            white-space: pre-wrap;
        `,
    },
    ccText: {
        css: css`
            margin-right: 15px;
            margin-left: 5px;
            margin-bottom: 10px;
            white-space: nowrap;
        `,
    },
    gridItem: {
        width: [12, 12, 12, 5, 5],
    },
    changeLink: {
        typographyProps: {
            size: "12px",
            lineHeight: "10px",
            css: css`
                color: ${getColor("text.main")} !important;
            `,
        },
        icon: {
            iconProps: {
                src: Edit,
                color: "primary.main",
                size: 16,
            },
            position: "left",
        },
        css: css`
            margin-top: 15px;
            margin-bottom: 5px;
            margin-right: 5px;
            &:hover > span,
            &:focus > span {
                color: ${getColor("primary")} !important;
            }
        `,
    },
    directionsLink: {
        typographyProps: {
            size: "12px",
            lineHeight: "10px",
            css: css`
                color: ${getColor("text.main")} !important;
            `,
        },
        icon: {
            iconProps: {
                src: Directions,
                color: "primary.main",
                size: 19,
                css: css`
                    margin-right: 7px;
                `,
            },
            position: "left",
        },
        css: css`
            &:hover > span,
            &:focus > span {
                color: ${getColor("primary")} !important;
            }
        `,
    },
    pickupInstructionsModal: {
        sizeVariant: "small",
    },
};

const mapStateToProps = (state: ApplicationState & xCheckoutState) => {
    const { cartId, requestedDeliveryDate, requestedPickupDate } = state.pages.checkoutReviewAndSubmit;
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;

    return {
        checkoutShippingPage: getPageLinkByPageType(state, "CheckoutShippingPage"),
        isPickUp: state.context.session.fulfillmentMethod === FulfillmentMethod.PickUp,

        cart,
        billTo: getBillToState(state, cart?.billToId)?.value,
        shipTo: getShipToState(state, cart?.shipToId)?.value,
        session: state.context.session,

        requestedDeliveryDate,
        requestedPickupDate,
        notes: state.xCheckout.notes,

        lastFour: state.xCheckout.lastFour,
        expDate: state.xCheckout.expDate,
        ccType: state.xCheckout.ccType,
        po: state.xCheckout.po,
        paymentMethod: state.xCheckout.paymentMethod,
        paymentProfile: state.xCheckout.paymentProfile,
        addressFieldDataView: getAddressFieldsDataView(state),
        state,
        customSettings: getSettingsCollection(state).customSettings as any,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & DispatchProp<xHeaderActions>;

const CheckoutReview = ({
    fields,
    checkoutShippingPage,
    isPickUp,
    cart,
    billTo,
    shipTo,
    session,
    requestedDeliveryDate,
    requestedPickupDate,
    notes,
    lastFour,
    expDate,
    ccType,
    po,
    paymentMethod,
    paymentProfile,
    addressFieldDataView,
    state,
    customSettings,
}: Props) => {
    const { currentStep, maxStep, setCurrentStep } = useContext(StepperContext);

    const [isFindLocationOpen, setIsFindLocationOpen] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);

    const [isPickupInstructionsModalOpen, setIsPickupInstructionsModalOpen] = React.useState(false);
    const toasterContext = React.useContext(ToasterContext);

    const handleOpenFindLocation = () => {
        setIsFindLocationOpen(true);
    };
    const handleFindLocationModalClose = () => setIsFindLocationOpen(false);

    const updatePickUpSelectedWarehouse = async (pickUpWarehouse: WarehouseModel) => {
        await updatePickUpWarehouse({ pickUpWarehouse });
    };

    const handleWarehouseSelected = (pickUpWarehouse: WarehouseModel) => {
        setIsLoading(true);
        updatePickUpSelectedWarehouse(pickUpWarehouse).then(result => {
            toasterContext.addToast({
                body: translate("Pick Up Address Updated"),
                messageType: "success",
            });
            setIsFindLocationOpen(false);
            setIsLoading(false);
        });
    };

    const location = session.pickUpWarehouse;
    const linkAddress = `http://maps.google.com/maps?daddr=${location?.address1} ${location?.address2}, ${location?.city}, ${location?.state} ${location?.postalCode}`;

    const currentCarrierIdx = cart?.carriers?.findIndex(c => c.id === cart?.carrier?.id);
    const carrierAccount =
        currentCarrierIdx === 1 ? state.xCheckout.carrierAccount || cart?.properties.carrierAccount : "";
    const additionalDetails =
        currentCarrierIdx === 2 ? state.xCheckout.additionalDetails || cart?.properties.additionalDetails : "";

    const shipCodeForIncoTermsDisplay = customSettings?.shipCodeForIncoTermsDisplay?.split(",");
    let incoTerms;
    if (
        currentCarrierIdx === 0 &&
        shipCodeForIncoTermsDisplay?.includes(cart?.properties?.shipCode) &&
        cart?.properties?.billToCarrier === cart?.carrier?.description
    ) {
        incoTerms = billTo?.properties?.carrierAccounts;
    }

    return (
        <>
            <StyledWrapper {...styles.wrapper}>
                <Link
                    href={checkoutShippingPage?.url}
                    {...styles.editLink}
                    data-test-selector="checkout_edit_addresses"
                >
                    {translate("Edit Addresses")}
                </Link>
                <GridContainer>
                    <GridItem {...styles.gridItem}>
                        <StyledWrapper>
                            <Typography {...styles.headingText} as="h3">
                                {translate("Billing Address")}
                            </Typography>
                            {billTo && (
                                <AddressInfoDisplay
                                    {...billTo}
                                    state={billTo.state?.abbreviation}
                                    country={billTo.country?.abbreviation}
                                    extendedStyles={styles.addressDisplay}
                                    {...({ addressFields: addressFieldDataView?.value?.billToAddressFields } as any)}
                                />
                            )}
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.gridItem}>
                        <StyledWrapper>
                            <Typography {...styles.headingText} as="h3">
                                {translate(isPickUp ? "Pick Up Location" : "Shipping Address")}
                            </Typography>
                            {!isPickUp && shipTo && (
                                <AddressInfoDisplay
                                    {...shipTo}
                                    state={shipTo.state?.abbreviation}
                                    country={shipTo.country?.abbreviation}
                                    extendedStyles={styles.addressDisplay}
                                    {...({ addressFields: addressFieldDataView?.value?.shipToAddressFields } as any)}
                                />
                            )}
                            {isPickUp && session.pickUpWarehouse && (
                                <>
                                    <Typography as="p">
                                        {session.pickUpWarehouse.description || session.pickUpWarehouse.name}
                                    </Typography>
                                    <AddressInfoDisplay
                                        {...session.pickUpWarehouse}
                                        extendedStyles={styles.addressDisplay}
                                        {...({
                                            addressFields: addressFieldDataView?.value?.shipToAddressFields,
                                        } as any)}
                                    />
                                </>
                            )}
                            {isPickUp && (
                                <>
                                    {location?.hours && (
                                        <Link
                                            onClick={() => setIsPickupInstructionsModalOpen(true)}
                                            {...styles.changeLink}
                                            data-test-selector="checkout_pickUpInstructions"
                                        >
                                            {translate("Pickup Instructions")}
                                        </Link>
                                    )}
                                    <Link
                                        href={linkAddress}
                                        {...styles.directionsLink}
                                        target="_blank"
                                        data-test-selector="checkout_pickUpLocation_directions"
                                    >
                                        {translate("Get Directions")}
                                    </Link>
                                    <FindLocationModal
                                        modalIsOpen={isFindLocationOpen}
                                        onWarehouseSelected={handleWarehouseSelected}
                                        onModalClose={handleFindLocationModalClose}
                                        extendedStyles={styles.findLocationModal}
                                        {...({ isWarehouseSelectedLoading: isLoading } as any)}
                                    />
                                </>
                            )}
                        </StyledWrapper>
                    </GridItem>
                </GridContainer>
            </StyledWrapper>

            <StyledWrapper {...styles.wrapper}>
                <Link
                    onClick={() => setCurrentStep(2)}
                    {...styles.editLink}
                    data-test-selector="checkout_edit_shipping"
                >
                    <Hidden above="md">{translate("Edit")}</Hidden>
                    <Hidden below="lg">
                        {translate(isPickUp ? "Edit Pick Up Information" : "Edit Shipping Information")}
                    </Hidden>
                </Link>
                <Typography {...styles.headingText} as="h3">
                    {translate(isPickUp ? "Pick Up Information" : "Shipping Information")}
                </Typography>

                {!isPickUp && (
                    <GridContainer>
                        <GridItem {...styles.gridItem}>
                            <Typography as="strong" {...styles.bodyText}>
                                {translate("Method:")}
                            </Typography>
                            <Typography>
                                {cart?.carrier?.description}
                                {currentCarrierIdx !== 2 ? ` / ${cart?.shipVia?.description}` : ""}
                                {incoTerms ? ` / ${incoTerms}` : ""}
                            </Typography>
                        </GridItem>
                        {carrierAccount && (
                            <GridItem
                                {...styles.gridItem}
                                css={css`
                                    @media (max-width: 768px) {
                                        padding-top: 0;
                                    }
                                `}
                            >
                                <Typography as="strong" {...styles.bodyText}>
                                    {translate("Carrier Account:")}
                                </Typography>
                                <Typography {...styles.valueText}>{carrierAccount}</Typography>
                            </GridItem>
                        )}
                        {additionalDetails && (
                            <GridItem {...styles.gridItem}>
                                <Typography as="strong" {...styles.bodyText}>
                                    {translate("Additional Details:")}
                                </Typography>
                                <Typography {...styles.valueText}>{additionalDetails}</Typography>
                            </GridItem>
                        )}
                    </GridContainer>
                )}

                <GridContainer>
                    {!isPickUp && (
                        <GridItem {...styles.gridItem}>
                            <Typography as="strong" {...styles.bodyText}>
                                {translate("Request Ship Date:")}
                            </Typography>
                            <Typography {...styles.valueText}>
                                {requestedDeliveryDate ? (
                                    <LocalizedDateTime dateTime={requestedDeliveryDate} />
                                ) : (
                                    translate("not entered")
                                )}
                            </Typography>
                        </GridItem>
                    )}
                    {isPickUp && (
                        <GridItem {...styles.gridItem}>
                            <Typography as="strong" {...styles.bodyText}>
                                {translate("Requested Pick Up Date:")}
                            </Typography>
                            <Typography {...styles.valueText}>
                                {requestedPickupDate ? (
                                    <LocalizedDateTime dateTime={requestedPickupDate} />
                                ) : (
                                    translate("not entered")
                                )}
                            </Typography>
                        </GridItem>
                    )}
                    <GridItem
                        {...styles.gridItem}
                        css={css`
                            @media (max-width: 768px) {
                                padding-top: 0;
                            }
                        `}
                    >
                        <Typography as="strong" {...styles.bodyText}>
                            {translate("Notes:")}
                        </Typography>
                        <Typography {...styles.valueText}>{notes || translate("not entered")}</Typography>
                    </GridItem>
                </GridContainer>
            </StyledWrapper>

            <StyledWrapper {...styles.wrapper}>
                <Link onClick={() => setCurrentStep(3)} {...styles.editLink} data-test-selector="checkout_edit_payment">
                    <Hidden above="md">{translate("Edit")}</Hidden>
                    <Hidden below="lg">{translate("Edit Payment Information")}</Hidden>
                </Link>
                <Typography {...styles.headingText} as="h3">
                    {translate("Payment Information")}
                </Typography>

                <GridContainer>
                    {paymentMethod !== "PO" && lastFour && expDate && (
                        <GridItem {...styles.gridItem}>
                            <Typography as="strong" {...styles.bodyText}>
                                {translate("Credit Card:")}
                            </Typography>
                            <StyledWrapper>
                                <Typography {...styles.ccText}>
                                    {ccType}, ****{lastFour}
                                </Typography>
                                <Typography {...styles.bodyText}>Exp {expDate} </Typography>
                            </StyledWrapper>
                        </GridItem>
                    )}
                    {paymentProfile && (
                        <GridItem {...styles.gridItem}>
                            <Typography as="strong" {...styles.bodyText}>
                                {translate("Paid with Profile:")}
                            </Typography>
                            <Typography {...styles.valueText}>{paymentProfile}</Typography>
                        </GridItem>
                    )}
                    {po && (
                        <GridItem {...styles.gridItem}>
                            <Typography as="strong" {...styles.bodyText}>
                                {translate("Purchase Order:")}
                            </Typography>
                            <Typography {...styles.valueText}>{po}</Typography>
                        </GridItem>
                    )}
                    {paymentMethod === "PO" && (
                        <GridItem {...styles.gridItem}>
                            <Typography {...styles.valueText}>{siteMessage("Custom_Paid_With_Terms")}</Typography>
                        </GridItem>
                    )}
                    {paymentMethod !== "PO" && !paymentProfile && !lastFour && (
                        <GridItem {...styles.gridItem}>
                            <Typography {...styles.valueText}>{siteMessage("Custom_Paid_With_CreditCard")}</Typography>
                        </GridItem>
                    )}
                </GridContainer>
                <Modal
                    headline={translate("Hours")}
                    {...styles.pickupInstructionsModal}
                    isOpen={isPickupInstructionsModalOpen}
                    handleClose={() => setIsPickupInstructionsModalOpen(false)}
                >
                    {location?.hours}
                </Modal>
            </StyledWrapper>
        </>
    );
};

const definition: WidgetDefinition = {
    group: "Checkout - Shipping",
    allowedContexts: [CheckoutShippingPageContext, CheckoutReviewAndSubmitPageContext],
    displayName: "Checkout Review",
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CheckoutReview),
    definition,
};

export default widgetModule;
